import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Settings, MapPin, RefreshCw, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import { apiFetch, apiFetchWithTimeout } from '@/utils/api';

interface Currency {
    id: string;
    code: string;
    name: string;
    symbol?: string;
    is_base_currency: boolean;
    is_active: boolean;
}

interface FruitBICredentials {
    id: string;
    name: string;
    database_name?: string;
    default_currency_id?: string;
    auto_sync: boolean;
    sync_orders: boolean;
    sync_inventory: boolean;
    is_active: boolean;
    test_connection_status?: string;
    test_connection_message?: string;
    last_test_date?: string;
    last_sync_date?: string;
    created_at: string;
    updated_at?: string;
}

interface FruitBILocation {
    id: string;
    ext_restaurant_id: string;
    ext_restaurant_name: string;
    ext_site_id: string;
    ext_site_name: string;
    cost_center_id?: string;
    created_at?: string;
}

interface CostCenter {
    id: string;
    name: string;
    address?: string;
    is_active: boolean;
}

export default function POSIntegrationsPage() {
    const [credentials, setCredentials] = useState<FruitBICredentials[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showLocationsModal, setShowLocationsModal] = useState(false);
    const [selectedCredentials, setSelectedCredentials] = useState<FruitBICredentials | null>(null);
    const [locations, setLocations] = useState<FruitBILocation[]>([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [syncing, setSyncing] = useState<string | null>(null);
    const [syncingMonthly, setSyncingMonthly] = useState(false);
    const [syncingTransactions, setSyncingTransactions] = useState(false);
    const [processingData, setProcessingData] = useState(false);
    const [syncingMenuItems, setSyncingMenuItems] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Monthly sync state
    const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
    const [selectedMonth, setSelectedMonth] = useState<string>('');

    const [formData, setFormData] = useState({
        name: '',
        client_uid: '',
        api_key: '',
        username: '',
        password: '',
        database_name: '',
        default_currency_id: '',
        auto_sync: true,
        sync_orders: true,
        sync_inventory: true,
        is_active: true
    });

    const [editFormData, setEditFormData] = useState({
        name: '',
        database_name: '',
        default_currency_id: '',
        auto_sync: true,
        sync_orders: true,
        sync_inventory: true,
        is_active: true
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [credentialsRes, currenciesRes, costCentersRes] = await Promise.all([
                apiFetch('/api/fruit-bi-credentials/'),
                apiFetch('/api/currencies/'),
                apiFetch('/api/cost-centers/')
            ]);

            if (credentialsRes.ok) {
                const credentialsData = await credentialsRes.json();
                setCredentials(credentialsData);
            }

            if (currenciesRes.ok) {
                const currenciesData = await currenciesRes.json();
                setCurrencies(currenciesData.filter((c: Currency) => c.is_active));
            }

            if (costCentersRes.ok) {
                const costCentersData = await costCentersRes.json();
                setCostCenters(costCentersData.filter((cc: CostCenter) => cc.is_active));
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        try {
            const response = await apiFetch('/api/fruit-bi-credentials/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setSuccess('Fruit BI credentials added successfully');
                setShowAddForm(false);
                setFormData({
                    name: '',
                    client_uid: '',
                    api_key: '',
                    username: '',
                    password: '',
                    database_name: '',
                    default_currency_id: '',
                    auto_sync: true,
                    sync_orders: true,
                    sync_inventory: true,
                    is_active: true
                });
                fetchData();
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to add credentials');
            }
        } catch (error) {
            console.error('Error adding credentials:', error);
            setError('Failed to add credentials');
        }
    };

    const syncLocations = async (credentialsId: string) => {
        setSyncing(credentialsId);
        setError(null);
        setSuccess(null);

        try {
            const response = await apiFetch(`/api/fruit-bi-credentials/${credentialsId}/sync-locations`, {
                method: 'POST',
            });

            if (response.ok) {
                const result = await response.json();
                setSuccess(`Synced ${result.locations_synced} locations successfully`);
                if (selectedCredentials?.id === credentialsId) {
                    fetchLocations(credentialsId);
                }
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to sync locations');
            }
        } catch (error) {
            console.error('Error syncing locations:', error);
            setError('Failed to sync locations');
        } finally {
            setSyncing(null);
        }
    };

    const fetchLocations = async (credentialsId: string) => {
        setLocationsLoading(true);
        try {
            const response = await apiFetch(`/api/fruit-bi-credentials/${credentialsId}/locations`);
            if (response.ok) {
                const data = await response.json();
                setLocations(data.locations);
            } else {
                setError('Failed to fetch locations');
            }
        } catch (error) {
            console.error('Error fetching locations:', error);
            setError('Failed to fetch locations');
        } finally {
            setLocationsLoading(false);
        }
    };

    const showLocations = async (creds: FruitBICredentials) => {
        setSelectedCredentials(creds);
        setShowLocationsModal(true);
        await fetchLocations(creds.id);
    };

    const openEditModal = (creds: FruitBICredentials) => {
        setSelectedCredentials(creds);
        setEditFormData({
            name: creds.name,
            database_name: creds.database_name || '',
            default_currency_id: creds.default_currency_id || '',
            auto_sync: creds.auto_sync,
            sync_orders: creds.sync_orders,
            sync_inventory: creds.sync_inventory,
            is_active: creds.is_active
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedCredentials) return;

        setError(null);
        setSuccess(null);

        try {
            const response = await apiFetch(`/api/fruit-bi-credentials/${selectedCredentials.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(editFormData),
            });

            if (response.ok) {
                setSuccess('Integration updated successfully');
                setShowEditModal(false);
                setSelectedCredentials(null);
                fetchData();
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to update integration');
            }
        } catch (error) {
            console.error('Error updating integration:', error);
            setError('Failed to update integration');
        }
    };

    const handleMonthlySyncOrders = async () => {
        if (!selectedMonth) {
            setError('Please select a month');
            return;
        }

        setSyncingMonthly(true);
        setError(null);
        setSuccess(null);

        try {
            // Use extended timeout (15 minutes) for sync orders operation
            const response = await apiFetchWithTimeout('/api/fruit-bi-credentials/sync-orders-monthly', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    year: parseInt(selectedYear),
                    month: parseInt(selectedMonth)
                }),
            }, 15 * 60 * 1000); // 15 minutes timeout

            if (response.ok) {
                const result = await response.json();
                if (result.status === 'started') {
                    setSuccess(`Sync started in background for ${result.integrations_count} integrations for ${selectedMonth}/${selectedYear}. Sync ID: ${result.sync_id}. Check sync status for progress.`);
                } else if (result.status === 'no_work') {
                    setSuccess(result.message);
                } else {
                    setSuccess(result.message);
                }
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to sync orders');
            }
        } catch (error) {
            console.error('Error syncing orders:', error);
            if (error instanceof Error && error.message && error.message.includes('timed out')) {
                setError(`Sync operation timed out. The process may still be running in the background. Please check the results later.`);
            } else {
                setError('Failed to sync orders');
            }
        } finally {
            setSyncingMonthly(false);
        }
    };

    const handleTransactionsSync = async () => {
        if (!selectedMonth) {
            setError('Please select a month');
            return;
        }

        setSyncingTransactions(true);
        setError(null);
        setSuccess(null);

        try {
            // Use extended timeout (15 minutes) for sync transactions operation
            const response = await apiFetchWithTimeout('/api/fruit-bi-credentials/sync-transactions-monthly', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    year: parseInt(selectedYear),
                    month: parseInt(selectedMonth)
                }),
            }, 15 * 60 * 1000); // 15 minutes timeout

            if (response.ok) {
                const result = await response.json();
                if (result.status === 'started') {
                    setSuccess(`Transactions sync started in background for ${result.integrations_count} integrations for ${selectedMonth}/${selectedYear}. Sync ID: ${result.sync_id}. Check sync status for progress.`);
                } else if (result.status === 'no_work') {
                    setSuccess(result.message);
                } else {
                    setSuccess(result.message);
                }
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to sync transactions');
            }
        } catch (error) {
            console.error('Error syncing transactions:', error);
            if (error instanceof Error && error.message && error.message.includes('timed out')) {
                setError(`Sync operation timed out. The process may still be running in the background. Please check the results later.`);
            } else {
                setError('Failed to sync transactions');
            }
        } finally {
            setSyncingTransactions(false);
        }
    };

    const handleProcessRawData = async () => {
        if (!selectedMonth) {
            setError('Please select a month');
            return;
        }

        setProcessingData(true);
        setError(null);
        setSuccess(null);

        try {
            // Use extended timeout (15 minutes) for process raw data operation
            const response = await apiFetchWithTimeout('/api/fruit-bi-credentials/process-raw-data-monthly', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    year: parseInt(selectedYear),
                    month: parseInt(selectedMonth)
                }),
            }, 15 * 60 * 1000); // 15 minutes timeout

            if (response.ok) {
                const result = await response.json();
                if (result.status === 'started') {
                    setSuccess(`Processing started in background for ${result.cost_centers_count} cost centers for ${selectedMonth}/${selectedYear}. Sync ID: ${result.sync_id}. Check sync status for progress.`);
                } else if (result.status === 'no_work') {
                    setSuccess(result.message);
                } else {
                    setSuccess(result.message);
                }
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to process raw data');
            }
        } catch (error) {
            console.error('Error processing raw data:', error);
            if (error instanceof Error && error.message && error.message.includes('timed out')) {
                setError(`Processing operation timed out. The process may still be running in the background. Please check the results later.`);
            } else {
                setError('Failed to process raw data');
            }
        } finally {
            setProcessingData(false);
        }
    };

    const handleSyncMenuItems = async () => {
        setSyncingMenuItems(true);
        setError(null);
        setSuccess(null);

        try {
            // Use extended timeout (10 minutes) for menu items sync operation
            const response = await apiFetchWithTimeout('/api/fruit-bi-credentials/sync-menu-items', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            }, 10 * 60 * 1000); // 10 minutes timeout

            if (response.ok) {
                const result = await response.json();
                if (result.status === 'started') {
                    setSuccess(`Menu items sync started for ${result.integrations_count} integrations and ${result.cost_centers_count} cost centers. Sync ID: ${result.sync_id}. Check sync status for progress.`);
                } else if (result.status === 'no_work') {
                    setSuccess(result.message);
                } else {
                    setSuccess(result.message);
                }
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to sync menu items');
            }
        } catch (error) {
            console.error('Error syncing menu items:', error);
            if (error instanceof Error && error.message && error.message.includes('timed out')) {
                setError(`Menu items sync operation timed out. The process may still be running in the background. Please check the results later.`);
            } else {
                setError('Failed to sync menu items');
            }
        } finally {
            setSyncingMenuItems(false);
        }
    };

    const mapLocationToCostCenter = async (locationId: string, costCenterId: string) => {
        try {
            const response = await apiFetch(`/api/fruit-bi-credentials/locations/${locationId}/cost-center`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ cost_center_id: costCenterId }),
            });

            if (response.ok) {
                setSuccess('Location mapped to cost center successfully');
                if (selectedCredentials) {
                    fetchLocations(selectedCredentials.id);
                }
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to map location');
            }
        } catch (error) {
            console.error('Error mapping location:', error);
            setError('Failed to map location');
        }
    };

    const deleteCredentials = async (credentialsId: string) => {
        if (!confirm('Are you sure you want to delete these credentials?')) {
            return;
        }

        try {
            const response = await apiFetch(`/api/fruit-bi-credentials/${credentialsId}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                setSuccess('Credentials deleted successfully');
                fetchData();
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to delete credentials');
            }
        } catch (error) {
            console.error('Error deleting credentials:', error);
            setError('Failed to delete credentials');
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">POS Integrations</h1>
                        <p className="text-muted-foreground">
                            Manage point-of-sale system integrations
                        </p>
                    </div>
                    <div className="space-y-4">
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-64 w-full" />
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">POS Integrations</h1>
                        <p className="text-muted-foreground">
                            Manage point-of-sale system integrations
                        </p>
                    </div>
                    <Button onClick={() => setShowAddForm(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Integration
                    </Button>
                </div>

                {/* Alerts */}
                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {success && (
                    <Alert>
                        <CheckCircle className="h-4 w-4" />
                        <AlertDescription>{success}</AlertDescription>
                    </Alert>
                )}

                {/* Monthly Orders Sync */}
                <Card>
                    <CardHeader>
                        <CardTitle>Monthly Orders Sync</CardTitle>
                        <CardDescription>
                            Step 1: Sync raw data from Fruit BI API for a specific month.
                            Step 2: Process the raw data into normalized orders and order items.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Year</Label>
                                    <Select value={selectedYear} onValueChange={setSelectedYear}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select year" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Array.from({ length: 5 }, (_, i) => {
                                                const year = new Date().getFullYear() - 2 + i;
                                                return (
                                                    <SelectItem key={year} value={year.toString()}>
                                                        {year}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Month</Label>
                                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select month" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Array.from({ length: 12 }, (_, i) => {
                                                const month = i + 1;
                                                const monthName = new Date(2024, i, 1).toLocaleString('default', { month: 'long' });
                                                return (
                                                    <SelectItem key={month} value={month.toString()}>
                                                        {monthName}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={handleMonthlySyncOrders}
                                    disabled={syncingMonthly || !selectedMonth}
                                    className="flex items-center gap-2"
                                >
                                    {syncingMonthly ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4" />
                                    )}
                                    {syncingMonthly ? 'Syncing Orders...' : 'Sync Orders'}
                                </Button>
                                <Button
                                    onClick={handleProcessRawData}
                                    disabled={processingData || !selectedMonth}
                                    variant="outline"
                                    className="flex items-center gap-2"
                                >
                                    {processingData ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4" />
                                    )}
                                    {processingData ? 'Processing Data...' : 'Process Data'}
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Transactions Sync */}
                <Card>
                    <CardHeader>
                        <CardTitle>Transactions Sync</CardTitle>
                        <CardDescription>
                            Sync transaction data (store endpoint) from Fruit BI API for a specific month.
                            This syncs inventory/store transactions separately from orders.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Year</Label>
                                    <Select value={selectedYear} onValueChange={setSelectedYear}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select year" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Array.from({ length: 5 }, (_, i) => {
                                                const year = new Date().getFullYear() - 2 + i;
                                                return (
                                                    <SelectItem key={year} value={year.toString()}>
                                                        {year}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Month</Label>
                                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select month" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Array.from({ length: 12 }, (_, i) => {
                                                const month = i + 1;
                                                const monthName = new Date(2024, i, 1).toLocaleString('default', { month: 'long' });
                                                return (
                                                    <SelectItem key={month} value={month.toString()}>
                                                        {monthName}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={handleTransactionsSync}
                                    disabled={syncingTransactions || !selectedMonth}
                                    className="flex items-center gap-2"
                                >
                                    {syncingTransactions ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4" />
                                    )}
                                    {syncingTransactions ? 'Syncing Transactions...' : 'Sync Transactions'}
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Menu Items Sync */}
                <Card>
                    <CardHeader>
                        <CardTitle>Menu Items Sync</CardTitle>
                        <CardDescription>
                            Sync menu items and product data from Fruit BI (biproducts.php endpoint and products section of bi.php).
                            This is a separate sync operation that focuses only on menu item data.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="flex justify-end">
                                <Button
                                    onClick={handleSyncMenuItems}
                                    disabled={syncingMenuItems}
                                    className="flex items-center gap-2"
                                >
                                    {syncingMenuItems ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4" />
                                    )}
                                    {syncingMenuItems ? 'Syncing Menu Items...' : 'Sync Menu Items'}
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Fruit BI Integrations */}
                <Card>
                    <CardHeader>
                        <CardTitle>Fruit BI Integrations</CardTitle>
                        <CardDescription>
                            Connect to Fruit BI POS systems to sync order and inventory data
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {credentials.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-muted-foreground">No integrations configured</p>
                                <Button
                                    onClick={() => setShowAddForm(true)}
                                    className="mt-4"
                                    variant="outline"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add Your First Integration
                                </Button>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Database</TableHead>
                                        <TableHead>Currency</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Sync Settings</TableHead>
                                        <TableHead>Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {credentials.map((cred) => (
                                        <TableRow key={cred.id}>
                                            <TableCell className="font-medium">{cred.name}</TableCell>
                                            <TableCell>{cred.database_name || '-'}</TableCell>
                                            <TableCell>
                                                {cred.default_currency_id ? (
                                                    currencies.find(c => c.id === cred.default_currency_id)?.code || 'Unknown'
                                                ) : (
                                                    <span className="text-muted-foreground">Not set</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={cred.is_active ? "default" : "secondary"}>
                                                    {cred.is_active ? "Active" : "Inactive"}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    {cred.sync_orders && <Badge variant="outline" className="text-xs">Orders</Badge>}
                                                    {cred.sync_inventory && <Badge variant="outline" className="text-xs">Inventory</Badge>}
                                                    {cred.auto_sync && <Badge variant="outline" className="text-xs">Auto</Badge>}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => syncLocations(cred.id)}
                                                        disabled={syncing === cred.id}
                                                        title="Sync locations"
                                                    >
                                                        {syncing === cred.id ? (
                                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <RefreshCw className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => showLocations(cred)}
                                                        title="View and map locations"
                                                    >
                                                        <MapPin className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => openEditModal(cred)}
                                                        title="Edit integration settings"
                                                    >
                                                        <Settings className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => deleteCredentials(cred.id)}
                                                        title="Delete integration"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Add Credentials Dialog */}
            <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Add Fruit BI Integration</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="name">Integration Name</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g., Main Restaurant"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="database_name">Database Name</Label>
                                <Input
                                    id="database_name"
                                    value={formData.database_name}
                                    onChange={(e) => setFormData({ ...formData, database_name: e.target.value })}
                                    placeholder="Optional"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="client_uid">Client UID</Label>
                                <Input
                                    id="client_uid"
                                    value={formData.client_uid}
                                    onChange={(e) => setFormData({ ...formData, client_uid: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="api_key">API Key</Label>
                                <Input
                                    id="api_key"
                                    type="password"
                                    value={formData.api_key}
                                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="username">Username (Optional)</Label>
                                <Input
                                    id="username"
                                    value={formData.username}
                                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label htmlFor="password">Password (Optional)</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="default_currency_id">Default Currency</Label>
                            <Select
                                value={formData.default_currency_id}
                                onValueChange={(value) => setFormData({ ...formData, default_currency_id: value })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select currency" />
                                </SelectTrigger>
                                <SelectContent>
                                    {currencies.map((currency) => (
                                        <SelectItem key={currency.id} value={currency.id}>
                                            {currency.code} - {currency.name}
                                            {currency.is_base_currency && ' (Base)'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                                Cancel
                            </Button>
                            <Button type="submit">Add Integration</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Locations Modal */}
            <Dialog open={showLocationsModal} onOpenChange={setShowLocationsModal}>
                <DialogContent className="max-w-max">
                    <DialogHeader>
                        <DialogTitle>
                            Locations - {selectedCredentials?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {locationsLoading ? (
                            <div className="space-y-2">
                                <Skeleton className="h-8 w-full" />
                                <Skeleton className="h-8 w-full" />
                                <Skeleton className="h-8 w-full" />
                            </div>
                        ) : locations.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-muted-foreground">No locations found</p>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Try syncing locations first
                                </p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Restaurant</TableHead>
                                        <TableHead>Site</TableHead>
                                        <TableHead>Cost Center</TableHead>
                                        <TableHead>Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {locations.map((location) => (
                                        <TableRow key={location.id}>
                                            <TableCell>
                                                <div>
                                                    <div className="font-medium">{location.ext_restaurant_name}</div>
                                                    <div className="text-sm text-muted-foreground">ID: {location.ext_restaurant_id}</div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    <div className="font-medium">{location.ext_site_name}</div>
                                                    <div className="text-sm text-muted-foreground">ID: {location.ext_site_id}</div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {location.cost_center_id ? (
                                                    <Badge variant="default">
                                                        {costCenters.find(cc => cc.id === location.cost_center_id)?.name || 'Unknown'}
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="secondary">Not mapped</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Select
                                                    value={location.cost_center_id || ''}
                                                    onValueChange={(value) => mapLocationToCostCenter(location.id, value)}
                                                >
                                                    <SelectTrigger className="w-48">
                                                        <SelectValue placeholder="Map to cost center" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {costCenters.map((cc) => (
                                                            <SelectItem key={cc.id} value={cc.id}>
                                                                {cc.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Integration Dialog */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Edit Integration - {selectedCredentials?.name}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEditSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="edit_name">Integration Name</Label>
                                <Input
                                    id="edit_name"
                                    value={editFormData.name}
                                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                    placeholder="e.g., Main Restaurant"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="edit_database_name">Database Name</Label>
                                <Input
                                    id="edit_database_name"
                                    value={editFormData.database_name}
                                    onChange={(e) => setEditFormData({ ...editFormData, database_name: e.target.value })}
                                    placeholder="Optional"
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="edit_default_currency_id">Default Currency</Label>
                            <Select
                                value={editFormData.default_currency_id || "none"}
                                onValueChange={(value) => setEditFormData({ ...editFormData, default_currency_id: value === "none" ? "" : value })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select currency" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">No default currency</SelectItem>
                                    {currencies.map((currency) => (
                                        <SelectItem key={currency.id} value={currency.id}>
                                            {currency.code} - {currency.name}
                                            {currency.is_base_currency && ' (Base)'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-3">
                            <Label>Sync Settings</Label>
                            <div className="flex flex-col space-y-2">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="edit_sync_orders"
                                        checked={editFormData.sync_orders}
                                        onChange={(e) => setEditFormData({ ...editFormData, sync_orders: e.target.checked })}
                                        className="rounded"
                                    />
                                    <Label htmlFor="edit_sync_orders">Sync Orders</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="edit_sync_inventory"
                                        checked={editFormData.sync_inventory}
                                        onChange={(e) => setEditFormData({ ...editFormData, sync_inventory: e.target.checked })}
                                        className="rounded"
                                    />
                                    <Label htmlFor="edit_sync_inventory">Sync Inventory</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="edit_auto_sync"
                                        checked={editFormData.auto_sync}
                                        onChange={(e) => setEditFormData({ ...editFormData, auto_sync: e.target.checked })}
                                        className="rounded"
                                    />
                                    <Label htmlFor="edit_auto_sync">Auto Sync</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="edit_is_active"
                                        checked={editFormData.is_active}
                                        onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                                        className="rounded"
                                    />
                                    <Label htmlFor="edit_is_active">Active</Label>
                                </div>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                                Cancel
                            </Button>
                            <Button type="submit">Update Integration</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

export const Route = createFileRoute('/sales/integrations')({
    component: POSIntegrationsPage,
})
